import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Available languages with translations
const resources = {
  'en-US': {
    translation: {
      // Language selection
      'language.english': 'English (US)',
      'language.arabic': 'Arabic',
      
      // Common UI elements
      'common.join': 'Join',
      'common.leave': 'Leave',
      'common.cancel': 'Cancel',
      'common.submit': 'Submit',
      'common.loading': 'Loading...',
      'common.error': 'An error occurred',
      'common.success': 'Success',
      'common.yes': 'Yes',
      'common.no': 'No',
      'common.connecting': 'Connecting...',
      
      // Login
      'login.portalTitle': 'Interview Portal',
      'login.subtitle': 'Start your voice interview',
      'login.usernameLabel': 'Username',
      'login.usernamePlaceholder': 'Enter your name',
      'login.emailLabel': 'Email',
      'login.emailPlaceholder': 'Enter your email',
      'login.passwordLabel': 'Password',
      'login.passwordPlaceholder': 'Enter your password',
      'login.appIdLabel': 'App ID',
      'login.appIdPlaceholder': 'Enter App ID',
      'login.apiKeyLabel': 'API Key',
      'login.apiKeyPlaceholder': 'Enter API Key',
      'login.startInterview': 'Start Interview',
      
      // Auth related
      'auth.username': 'Username',
      'auth.password': 'Password',
      'auth.login': 'Login',
      'auth.logout': 'Logout',
      'auth.register': 'Register',
      'auth.guest': 'Continue as Guest',
      
      // Call related
      'call': {
        'voiceConnectionRequired': 'Voice Connection Required',
        'connectToUse': 'Please connect to voice to use the Call Interface',
        'goToChat': 'Go to Chat',
        'status': {
          'connected': 'Connected',
          'connecting': 'Connecting',
          'live': 'Live'
        },
        'camera': {
          'on': 'Camera On',
          'off': 'Camera Off',
          'enable': 'Turn On Camera',
          'disable': 'Turn Off Camera',
          'enableHint': 'Turn on your camera to see your preview',
          'connecting': 'Connecting Camera...',
          'loading': 'This may take a moment'
        },
        'microphone': {
          'muted': 'Muted',
          'unmuted': 'Unmuted',
          'speaking': 'Speaking',
          'mute': 'Mute',
          'unmute': 'Unmute'
        },
        'leave': 'Leave',
        'labels': {
          'you': 'You',
          'aiAssistant': 'AI Assistant',
          'tryAgain': 'Try Again'
        }
      },
      
      // Chat related
      'chat.you': 'You',
      'chat.interviewer': 'Interviewer',
      'chat.assistantTitle': 'Voice Assistant Chat',
      'chat.status': {
        'connected': 'Connected',
        'connecting': 'Connecting...',
        'disconnected': 'Disconnected',
        'error': 'Error'
      },
      'chat.message': 'Message',
      'chat.send': 'Send',
      'chat.typing': 'typing...',
      'chat.online': 'Online',
      'chat.offline': 'Offline',
      'chat.buttons': {
        'clearChat': 'Clear Chat',
        'clearChatTitle': 'Clear the chat history',
        'testData': 'Send Test Data',
        'testDataTitle': 'Send test runtime data to the server'
      },
      'chat.testData': {
        'profileButton': 'Edit Profile Button',
        'settingsButton': 'Settings Button',
        'homeScreen': 'Home'
      },
      'chat.debug': {
        'testingRuntimeData': 'Testing runtime data send...',
        'runtimeDataSent': 'Test runtime data sent successfully:'
      },
      'chat.errors': {
        'noSession': 'No session found for runtime data test',
        'runtimeDataFailed': 'Failed to send test runtime data'
      },
      'chat.messages': {
        'runtimeDataSent': 'Runtime data sent successfully!'
      },
      
      // Transcript related
      'transcript': {
        'title': 'Interview Transcript',
        'empty': 'No transcript available yet.',
        'noData': 'No transcript data to download',
        'confirmClear': 'Are you sure you want to clear the transcript? This action cannot be undone.',
        'speakers': {
          'interviewer': 'Interviewer',
          'candidate': 'Candidate',
          'system': 'System',
          'ai': 'AI Assistant'
        },
        'buttons': {
          'download': 'Download Transcript',
          'downloadTitle': 'Download transcript as text file',
          'clear': 'Clear',
          'clearTitle': 'Clear the transcript'
        }
      },
      
      // JD Upload Modal
      'jdModal': {
        'title': 'Upload Job Description',
        'description': 'Add a job description to help the AI understand the role and requirements for the interview.',
        'tabs': {
          'enterText': 'Generate JD',
          'uploadFile': 'Upload File'
        },
        'labels': {
          'jobTitle': 'Job Title',
          'companyName': 'Company Name',
          'jobDescription': 'Job Description',
          'fileUpload': 'Upload PDF File'
        },
        'placeholders': {
          'jobTitle': 'e.g., Senior Frontend Developer',
          'companyName': 'e.g., Tech Corp Inc.',
          'jobDescription': 'Paste the job description content here...'
        },
        'fileUpload': {
          'instructions': 'Click to select a PDF file or drag and drop it here',
          'selectedFile': 'Selected file',
          'maxSize': 'Maximum file size: 5MB',
          'fileType': 'PDF files only'
        },
        'buttons': {
          'upload': 'Upload',
          'cancel': 'Cancel',
          'close': 'Close'
        },
        'errors': {
          'jobTitleRequired': 'Job title is required',
          'contentRequired': 'Job description content is required',
          'fileRequired': 'Please select a file to upload',
          'pdfOnly': 'Please select a PDF file only',
          'fileSize': 'File size must be less than 5MB',
          'uploadFailed': 'Failed to upload job description'
        },
        'success': {
          'title': 'Success',
          'message': 'Job description uploaded successfully!',
          'processing': 'Processing your job description...'
        },
        'uploadRequirements': {
          'title': 'Requirements:',
          'jobTitle': '✓ Job title is required',
          'jobDescription': '✓ Job description is required',
          'pdfOnly': '✓ PDF format only',
          'fileSize': '✓ Max file size: 5MB',
          'aiAnalysis': '✓ AI will analyze the content to tailor the interview'
        }
      },
      
      // Interview related
      'interview.start': 'Start Interview',
      'interview.end': 'End Interview',
      'interview.recording': 'Recording...',
      'interview.record': 'Record',
      'interview.stop': 'Stop',
      'interview.time': 'Time',
      'interview.duration': 'Duration',
      'interview.viewTranscript': 'View Transcript',
      'interview.leaveInterview': 'Leave Interview',
      'interview.title': 'Interview',
      // Settings
      'settings.title': 'Settings',
      'settings.language': 'Language',
      'settings.theme': 'Theme',
      'settings.notifications': 'Notifications',
      'settings.privacy': 'Privacy',
      'settings.help': 'Help',
      'settings.about': 'About',
      
      // Resume Upload Modal
      'resumeModal': {
        'title': 'Resume Required',
        'description': 'To start your interview, please upload your resume first. Your resume will be analyzed to create personalized interview questions.',
        'chooseFile': 'Choose PDF file',
        'requirements': {
          'title': 'Requirements:',
          'pdfOnly': 'PDF format only',
          'maxSize': 'Maximum 10MB file size',
          'aiProcessing': 'Resume will be processed with AI for question generation'
        },
        'buttons': {
          'cancel': 'Cancel',
          'upload': 'Upload Resume',
          'uploading': 'Uploading & Processing...'
        },
        'success': {
          'title': 'Resume Uploaded Successfully!',
          'message': 'Your resume has been processed. Starting your interview...'
        },
        'errors': {
          'pdfOnly': 'Please select a PDF file only',
          'fileSize': 'File size must be less than 10MB',
          'noFile': 'Please select a file first',
          'uploadFailed': 'Resume processing failed. Please try uploading again.'
        }
      }
    }
  },
  'ar-SA': {
    translation: {
      // Language selection
      'language.english': 'الإنجليزية',
      'language.arabic': 'العربية',
      // Common UI elements
      'common.join': 'انضم',
      'common.leave': 'مغادرة',
      'common.cancel': 'إلغاء',
      'common.submit': 'إرسال',
      'common.loading': 'جاري التحميل...',
      'common.error': 'حدث خطأ',
      'common.success': 'تم بنجاح',
      'common.yes': 'نعم',
      'common.no': 'لا',
      'common.connecting': 'جاري الاتصال...',
      
      // Login
      'login.portalTitle': 'بوابة المقابلة',
      'login.subtitle': 'ابدأ مقابلتك الصوتية',
      'login.usernameLabel': 'اسم المستخدم',
      'login.usernamePlaceholder': 'أدخل اسمك',
      'login.emailLabel': 'البريد الإلكتروني',
      'login.emailPlaceholder': 'أدخل بريدك الإلكتروني',
      'login.passwordLabel': 'كلمة المرور',
      'login.passwordPlaceholder': 'أدخل كلمة المرور',
      'login.appIdLabel': 'معرف التطبيق',
      'login.appIdPlaceholder': 'أدخل معرف التطبيق',
      'login.apiKeyLabel': 'مفتاح API',
      'login.apiKeyPlaceholder': 'أدخل مفتاح API',
      'login.startInterview': 'بدء المقابلة',
      
      // Auth related
      'auth.username': 'اسم المستخدم',
      'auth.password': 'كلمة المرور',
      'auth.login': 'تسجيل الدخول',
      'auth.logout': 'تسجيل الخروج',
      'auth.register': 'تسجيل',
      'auth.guest': 'المتابعة كضيف',
      
      // Call related
      'call': {
        'voiceConnectionRequired': 'مطلوب اتصال صوتي',
        'connectToUse': 'يرجى الاتصال بالصوت لاستخدام واجهة المكالمة',
        'goToChat': 'الذهاب إلى الدردشة',
        'status': {
          'connected': 'متصل',
          'connecting': 'جاري الاتصال',
          'live': 'مباشر'
        },
        'camera': {
          'on': 'الكاميرا تعمل',
          'off': 'الكاميرا مغلقة',
          'enable': 'تشغيل الكاميرا',
          'disable': 'إيقاف الكاميرا',
          'enableHint': 'قم بتشغيل الكاميرا لرؤية معاينتك',
          'connecting': 'جاري الاتصال بالكاميرا...',
          'loading': 'قد يستغرق هذا بضع لحظات'
        },
        'microphone': {
          'muted': 'صامت',
          'unmuted': 'غير صامت',
          'speaking': 'يتحدث',
          'mute': 'كتم',
          'unmute': 'إلغاء الكتم'
        },
        'leave': 'مغادرة',
        'labels': {
          'you': 'أنت',
          'aiAssistant': 'المساعد الذكي',
          'tryAgain': 'حاول مرة أخرى'
        }
      },
      
      // Chat related
      'chat.you': 'أنت',
      'chat.interviewer': 'المحاور',
      'chat.assistantTitle': 'دردشة المساعد الصوتي',
      'chat.status': {
        'connected': 'متصل',
        'connecting': 'جاري الاتصال...',
        'disconnected': 'غير متصل',
        'error': 'خطأ'
      },
      'chat.message': 'رسالة',
      'chat.send': 'إرسال',
      'chat.typing': 'يكتب...',
      'chat.online': 'متصل',
      'chat.offline': 'غير متصل',
      'chat.buttons': {
        'clearChat': 'مسح المحادثة',
        'clearChatTitle': 'مسح سجل المحادثة',
        'testData': 'إرسال بيانات اختبار',
        'testDataTitle': 'إرسال بيانات وقت تشغيل تجريبية إلى الخادم'
      },
      'chat.testData': {
        'profileButton': 'زر تعديل الملف الشخصي',
        'settingsButton': 'زر الإعدادات',
        'homeScreen': 'الرئيسية'
      },
      'chat.debug': {
        'testingRuntimeData': 'جاري اختبار إرسال بيانات وقت التشغيل...',
        'runtimeDataSent': 'تم إرسال بيانات وقت التشغيل بنجاح:'
      },
      'chat.errors': {
        'noSession': 'لم يتم العثور على جلسة لاختبار وقت التشغيل',
        'runtimeDataFailed': 'فشل إرسال بيانات وقت التشغيل التجريبية'
      },
      'chat.messages': {
        'runtimeDataSent': 'تم إرسال بيانات وقت التشغيل بنجاح!'
      },
      
      // Transcript related
      'transcript': {
        'title': 'نص المقابلة',
        'empty': 'لا يوجد نص متاح حتى الآن.',
        'noData': 'لا توجد بيانات نصية للتنزيل',
        'confirmClear': 'هل أنت متأكد من رغبتك في مسح النص؟ لا يمكن التراجع عن هذا الإجراء.',
        'speakers': {
          'interviewer': 'المحاور',
          'candidate': 'المتقدم',
          'system': 'النظام',
          'ai': 'المساعد الذكي'
        },
        'buttons': {
          'download': 'تنزيل النص',
          'downloadTitle': 'تنزيل النص كملف نصي',
          'clear': 'مسح',
          'clearTitle': 'مسح النص'
        }
      },
      
      // JD Upload Modal
      'jdModal': {
        'title': 'رفع وصف الوظيفة',
        'description': 'أضف وصفًا للوظيفة لمساعدة الذكاء الاصطناعي على فهم الدور والمتطلبات للمقابلة.',
        'tabs': {
          'enterText': 'إدخال النص',
          'uploadFile': 'رفع ملف'
        },
        'labels': {
          'jobTitle': 'المسمى الوظيفي',
          'companyName': 'اسم الشركة',
          'jobDescription': 'وصف الوظيفة',
          'fileUpload': 'رفع ملف PDF'
        },
        'placeholders': {
          'jobTitle': 'مثال: مطور واجهات أمامية أول',
          'companyName': 'مثال: شركة التقنية المحدودة',
          'jobDescription': 'الصق محتوى وصف الوظيفة هنا...'
        },
        'fileUpload': {
          'instructions': 'انقر لتحديد ملف PDF أو اسحبه وأسقطه هنا',
          'selectedFile': 'الملف المحدد',
          'maxSize': 'الحد الأقصى لحجم الملف: 5 ميجابايت',
          'fileType': 'ملفات PDF فقط'
        },
        'buttons': {
          'upload': 'رفع',
          'cancel': 'إلغاء',
          'close': 'إغلاق'
        },
        'errors': {
          'jobTitleRequired': 'المسمى الوظيفي مطلوب',
          'contentRequired': 'محتوى وصف الوظيفة مطلوب',
          'fileRequired': 'الرجاء اختيار ملف للرفع',
          'pdfOnly': 'الرجاء اختيار ملف PDF فقط',
          'fileSize': 'يجب أن يكون حجم الملف أقل من 5 ميجابايت',
          'uploadFailed': 'فشل رفع وصف الوظيفة'
        },
        'success': {
          'title': 'تم بنجاح',
          'message': 'تم رفع وصف الوظيفة بنجاح!',
          'processing': 'جاري معالجة وصف الوظيفة...'
        },
        'uploadRequirements': {
          'title': 'المتطلبات:',
          'jobTitle': '✓ المسمى الوظيفي مطلوب',
          'jobDescription': '✓ وصف الوظيفة مطلوب',
          'pdfOnly': '✓ صيغة PDF فقط',
          'fileSize': '✓ الحد الأقصى لحجم الملف: 5 ميجابايت',
          'aiAnalysis': '✓ سيقوم الذكاء الاصطناعي بتحليل المحتوى لتخصيص المقابلة'
        }
      },
      
      // Interview related
      'interview.start': 'بدء المقابلة',
      'interview.end': 'إنهاء المقابلة',
      'interview.recording': 'جاري التسجيل...',
      'interview.record': 'تسجيل',
      'interview.stop': 'إيقاف',
      'interview.time': 'الوقت',
      'interview.duration': 'المدة',
      'interview.viewTranscript': 'عرض الترجمة',
      'interview.leaveInterview': 'مغادرة المقابلة',
      'interview.title': 'الترجمة',
      // Settings
      'settings.title': 'الإعدادات',
      'settings.language': 'اللغة',
      'settings.theme': 'السمة',
      'settings.notifications': 'الإشعارات',
      'settings.privacy': 'الخصوصية',
      'settings.help': 'المساعدة',
      'settings.about': 'حول',
      
      // Resume Upload Modal
      'resumeModal': {
        'title': 'السيرة الذاتية مطلوبة',
        'description': 'لبدء مقابلتك، يرجى تحميل سيرتك الذاتية أولاً. سيتم تحليل سيرتك الذاتية لإنشاء أسئلة مقابلة مخصصة.',
        'chooseFile': 'اختر ملف PDF',
        'requirements': {
          'title': 'المتطلبات:',
          'pdfOnly': 'صيغة PDF فقط',
          'maxSize': 'الحد الأقصى لحجم الملف 10 ميجابايت',
          'aiProcessing': 'سيتم معالجة السيرة الذاتية باستخدام الذكاء الاصطناعي لإنشاء الأسئلة'
        },
        'buttons': {
          'cancel': 'إلغاء',
          'upload': 'رفع السيرة الذاتية',
          'uploading': 'جاري الرفع والمعالجة...'
        },
        'success': {
          'title': 'تم رفع السيرة الذاتية بنجاح!',
          'message': 'تم معالجة سيرتك الذاتية. بدء مقابلتك...'
        },
        'errors': {
          'pdfOnly': 'الرجاء اختيار ملف PDF فقط',
          'fileSize': 'يجب أن يكون حجم الملف أقل من 10 ميجابايت',
          'noFile': 'الرجاء اختيار ملف أولاً',
          'uploadFailed': 'فشلت معالجة السيرة الذاتية. يرجى المحاولة مرة أخرى.'
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en-US',
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
